import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
  CircularProgress,
  Autocomplete,
  Chip,
  IconButton,
  Avatar,
} from "@mui/material";
import { HiPlus, HiPhotograph, HiTrash } from "react-icons/hi";
import { useSnackbar } from "notistack";
import { useAdminCreateOrganization } from "@/api/hooks/admin.hooks";
import { getOrgInitials } from "@/utils/getOrgInitials";
import type { OrganizationDto } from "@/api/types/dtos";
import { useOrganizations } from "@/api/hooks/organization.hooks";

interface CreateOrganizationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const createMutation = useAdminCreateOrganization();

  const [name, setName] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [parentOrganizationId, setParentOrganizationId] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: parentOrgsData } = useOrganizations();
  const parentOrgs = (parentOrgsData?.data ?? []) as OrganizationDto[];

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      enqueueSnackbar("Solo se permiten archivos de imagen", { variant: "error" });
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleLogoRemove = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) {
      newErrors.name = "El nombre es obligatorio";
    }
    if (name.trim() && name.trim().length < 2) {
      newErrors.name = "El nombre debe tener al menos 2 caracteres";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    createMutation.mutate(
      {
        data: {
          name: name.trim(),
          idNumber: idNumber.trim(),
          parentOrganizationId: parentOrganizationId || undefined,
          isActive,
          logo: null,
        },
        logoFile,
      },
      {
        onSuccess: () => {
          enqueueSnackbar("Organización creada exitosamente", { variant: "success" });
          onSuccess();
        },
        onError: (error: unknown) => {
          const axiosError = error as {
            response?: { data?: { description?: string; message?: string } };
          };
          const message =
            axiosError?.response?.data?.description ||
            axiosError?.response?.data?.message ||
            "Error al crear la organización";
          enqueueSnackbar(message, { variant: "error" });
        },
      }
    );
  };

  const handleClose = () => {
    if (createMutation.isPending) return;
    setName("");
    setIdNumber("");
    setParentOrganizationId(null);
    setIsActive(true);
    setErrors({});
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="create-org-dialog-title"
      PaperProps={{
        sx: { borderTop: "4px solid", borderColor: "primary.main" },
      }}
    >
      <DialogTitle id="create-org-dialog-title" sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <HiPlus size={22} />
          <Box>
            <Typography variant="h6" component="span" fontWeight={600}>
              Nueva Organización
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Completa los campos para registrar una nueva organización
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Información General
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Nombre de la Organización"
                required
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                disabled={createMutation.isPending}
                placeholder="Ej: Cooperativa Central"
                id="create-org-name"
                inputProps={{
                  "aria-invalid": !!errors.name,
                  "aria-describedby": errors.name ? "create-org-name-error" : undefined,
                }}
                FormHelperTextProps={{ id: "create-org-name-error" }}
              />
              <TextField
                label="Número de Documento / Identificación"
                fullWidth
                value={idNumber}
                onChange={(e) => setIdNumber(e.target.value)}
                disabled={createMutation.isPending}
                placeholder="Ej: 900123456"
              />
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Logo
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {logoPreview ? (
                <Avatar src={logoPreview} sx={{ width: 64, height: 64 }} variant="rounded" />
              ) : (
                <Avatar
                  sx={{ width: 64, height: 64, bgcolor: "primary.main" }}
                  variant="rounded"
                >
                  <Typography variant="h6">{getOrgInitials(name)}</Typography>
                </Avatar>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoSelect}
                style={{ display: "none" }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<HiPhotograph size={16} />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={createMutation.isPending}
                >
                  {logoPreview ? "Cambiar" : "Subir"}
                </Button>
                {logoPreview && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={handleLogoRemove}
                    disabled={createMutation.isPending}
                  >
                    <HiTrash size={18} />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Relación Jerárquica
            </Typography>
            <Autocomplete
              options={parentOrgs}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={parentOrgs.find((o) => o.id === parentOrganizationId) || null}
              onChange={(_, value) => setParentOrganizationId(value?.id || null)}
              disabled={createMutation.isPending}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Organización Padre"
                  placeholder="Buscar organización..."
                  helperText="Déjalo vacío si es una organización principal"
                />
              )}
              renderOption={(props: any, option: OrganizationDto) => {
                const { key: _key, ...rest } = props;
                return (
                  <li key={option.id} {...rest}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span>{option.name}</span>
                      {!option.isActive && <Chip label="Inactiva" size="small" color="default" />}
                    </Box>
                  </li>
                );
              }}
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Estado
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  disabled={createMutation.isPending}
                  color="success"
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    label={isActive ? "Activa" : "Inactiva"}
                    color={isActive ? "success" : "default"}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              }
            />
          </Box>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={createMutation.isPending} sx={{ minWidth: 100 }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={createMutation.isPending}
          startIcon={
            createMutation.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <HiPlus size={16} />
            )
          }
          sx={{ minWidth: 140 }}
        >
          {createMutation.isPending ? "Creando..." : "Crear Organización"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateOrganizationModal;
